if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
        this.prependMedia = this.dataset.disablePrepend != 'true';

        if (this.productInfo && Shopify.postLinksRetry) {
          this.productInfo.initShareLinks();
        }

        this.filteringOption = this.dataset.filteringOption;

        this.initPurchaseModeSwaps();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener(
              'click',
              this.setActiveMedia.bind(
                this,
                mediaToSwitch.dataset.target,
                false
              )
            );
        });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      initPurchaseModeSwaps() {
        this.purchaseModeScope =
          this.closest('.shopify-section') ||
          this.productInfo ||
          document;

        this.lastPurchaseMode = null;

        this.syncPurchaseMode();
        this.syncLoopPrice();
        this.observeLoopWidget();

        this.purchaseModeHandler = () => {
          requestAnimationFrame(() => {
            this.syncPurchaseMode();
            this.syncLoopPrice();
            this.observeLoopWidget();
          });

          setTimeout(() => {
            this.syncPurchaseMode();
            this.syncLoopPrice();
            this.observeLoopWidget();
          }, 100);
        };

        this.purchaseModeScope.addEventListener(
          'click',
          this.purchaseModeHandler,
          true
        );

        this.purchaseModeScope.addEventListener(
          'change',
          this.purchaseModeHandler,
          true
        );

        setTimeout(() => {
          this.syncPurchaseMode();
          this.syncLoopPrice();
          this.observeLoopWidget();
        }, 300);
      }

      observeLoopWidget() {
        if (this.loopWidgetObserver) return;

        const loopWidget =
          this.purchaseModeScope.querySelector?.('.loop-widget-container');

        if (!loopWidget) return;

        this.loopWidgetObserver = new MutationObserver(() => {
          this.syncPurchaseMode();
          this.syncLoopPrice();
        });

        this.loopWidgetObserver.observe(loopWidget, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: [
            'value',
            'aria-checked',
            'class',
          ],
        });
      }

      getPurchaseMode() {
        const selectedLoopOption =
          this.purchaseModeScope.querySelector?.(
            '.loop-widget-purchase-option[aria-checked="true"]'
          );

        if (selectedLoopOption) {
          if (
            selectedLoopOption.hasAttribute(
              'data-loop-widget-onetime-purchase-option'
            ) ||
            selectedLoopOption.classList.contains(
              'loop-widget-purchase-option-onetime'
            )
          ) {
            return 'one-time';
          }

          if (
            selectedLoopOption.hasAttribute(
              'data-loop-widget-selling-plan-group'
            ) ||
            selectedLoopOption.hasAttribute(
              'data-selling-plan-group-id'
            )
          ) {
            return 'subscription';
          }
        }

        const loopWidget =
          this.purchaseModeScope.querySelector?.(
            '.loop-widget-container'
          );

        if (loopWidget) return null;

        const sellingPlanInput =
          this.purchaseModeScope.querySelector?.(
            'input[type="hidden"][name="selling_plan"]'
          );

        if (sellingPlanInput) {
          return sellingPlanInput.value.trim()
            ? 'subscription'
            : 'one-time';
        }

        return null;
      }

      syncPurchaseMode() {
        const mode = this.getPurchaseMode();

        if (!mode) return;

        if (mode !== this.lastPurchaseMode) {
          this.lastPurchaseMode = mode;

          const image =
            this.swapFirstMediaForMode(mode);

          if (
            this.classList.contains(
              'purchase-mode-pending'
            )
          ) {
            if (!image || image.complete) {
              this.revealPurchaseModeImage();
            } else {
              image.addEventListener(
                'load',
                () => this.revealPurchaseModeImage(),
                { once: true }
              );
            }
          }
        } else if (
          this.classList.contains(
            'purchase-mode-pending'
          )
        ) {
          this.revealPurchaseModeImage();
        }
      }

      revealPurchaseModeImage() {
        this.classList.remove(
          'purchase-mode-pending'
        );
      }

      syncLoopPrice() {
        if (!this.purchaseModeScope) return;

        const selectedOption =
          this.purchaseModeScope.querySelector?.(
            '.loop-widget-purchase-option[aria-checked="true"]'
          );

        if (!selectedOption) return;

        const loopPrice =
          selectedOption.querySelector(
            '.loop-widget-purchase-option-price'
          );

        if (!loopPrice) return;

        const price =
          loopPrice.textContent.trim();

        if (!price) return;

        const priceContainer =
          document.getElementById(
            `price-${this.dataset.section}`
          ) ||
          this.productInfo?.querySelector(
            '.product-page-price'
          );

        if (!priceContainer) return;

        priceContainer
          .querySelectorAll('.main-price')
          .forEach((element) => {
            if (
              element.textContent.trim() !== price
            ) {
              element.textContent = price;
            }
          });
      }

      getFirstSlideImage() {
        const firstSlide =
          this.elements.viewer?.querySelector(
            '.product__media-item'
          ) ||
          this.querySelector(
            '.product__media-item'
          );

        if (!firstSlide) return null;

        return (
          firstSlide.querySelector(
            '.product__modal-opener--image img'
          ) ||
          firstSlide.querySelector('img')
        );
      }

      getFirstThumbImage() {
        if (!this.elements.thumbnails) {
          return null;
        }

        const firstThumb =
          this.elements.thumbnails.querySelector(
            '[data-target]'
          ) ||
          this.elements.thumbnails.querySelector(
            '.thumbnail-list__item'
          );

        return (
          firstThumb?.querySelector('img') ||
          null
        );
      }

      swapImage(img, url) {
        if (!img || !url) return;

        img.src = url;
        img.setAttribute('src', url);
        img.setAttribute('srcset', url);

        if (img.hasAttribute('data-src')) {
          img.setAttribute(
            'data-src',
            url
          );
        }

        if (img.hasAttribute('data-srcset')) {
          img.setAttribute(
            'data-srcset',
            url
          );
        }

        const picture =
          img.closest('picture');

        if (picture) {
          picture
            .querySelectorAll('source')
            .forEach((source) => {
              source.setAttribute(
                'srcset',
                url
              );

              if (
                source.hasAttribute(
                  'data-srcset'
                )
              ) {
                source.setAttribute(
                  'data-srcset',
                  url
                );
              }
            });
        }
      }

      swapFirstMediaForMode(mode) {
        const targetMain =
          mode === 'subscription'
            ? this.dataset.subscriptionMainImg
            : this.dataset.oneTimeMainImg;

        const targetThumb =
          mode === 'subscription'
            ? this.dataset.subscriptionThumbImg
            : this.dataset.oneTimeThumbImg;

        const image =
          this.getFirstSlideImage();

        const thumbnail =
          this.getFirstThumbImage();

        if (targetMain) {
          this.swapImage(
            image,
            targetMain
          );
        }

        if (targetThumb) {
          this.swapImage(
            thumbnail,
            targetThumb
          );
        }

        return image;
      }

      onSlideChanged(event) {
        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(
        mediaId,
        prepend,
        filtering = false,
        currentVariant = null
      ) {
        if (
          filtering &&
          currentVariant
        ) {
          const allSlides =
            this.querySelectorAll(
              '.product__media-item, .thumbnail-list__item, .slider-counter__link'
            );

          allSlides.forEach((slide) => {
            if (
              slide.dataset.alt ===
                currentVariant[
                  this.filteringOption
                ] ||
              slide.dataset.alt ===
                'always_display'
            ) {
              slide.classList.remove(
                'hidden'
              );
            } else {
              slide.classList.add(
                'hidden'
              );
            }
          });
        }

        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          );

        this.elements.viewer
          .querySelectorAll(
            '[data-media-id]'
          )
          .forEach((element) => {
            element.classList.remove(
              'is-active'
            );
          });

        activeMedia.classList.add(
          'is-active'
        );

        if (
          prepend &&
          this.prependMedia
        ) {
          activeMedia.parentElement.prepend(
            activeMedia
          );

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            activeThumbnail.parentElement.prepend(
              activeThumbnail
            );
          }

          if (
            this.elements.viewer.slider
          ) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (
            this.elements.thumbnails
          ) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          if (
            !this.elements.thumbnails ||
            this.dataset.desktopLayout ===
              'stacked'
          ) {
            activeMedia.scrollIntoView({
              behavior: 'smooth',
            });
          }
        });

        this.playActiveMedia(
          activeMedia
        );

        if (
          filtering &&
          currentVariant
        ) {
          if (
            this.elements.viewer &&
            this.elements.viewer.initPages
          ) {
            this.elements.viewer.initPages();
          }

          if (
            this.elements.viewer &&
            this.elements.viewer.update
          ) {
            this.elements.viewer.update();
          }
        }

        if (
          !this.elements.thumbnails
        ) {
          return;
        }

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(
          activeThumbnail
        );

        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute(
              'aria-current'
            );
          });

        thumbnail
          .querySelector('button')
          .setAttribute(
            'aria-current',
            true
          );

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            false,
            10,
            true
          )
        ) {
          return;
        }

        if (
          this.elements.thumbnails.vertical
        ) {
          this.elements.thumbnails.slider.scrollTo({
            top: thumbnail.offsetTop,
          });
        } else {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
          });
        }
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(
            false
          );
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) {
          return;
        }

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }

      removeListSemantic() {
        if (
          !this.elements.viewer.slider
        ) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          }
        );
      }
    }
  );
}